import { useMemo } from 'react';
import { useScorecardFilterOrchestrator } from '@/creditflowresearch/application/League/Scorecard/hooks/useScorecardFilterOrchestrator';
import { useScorecardTable } from '@/creditflowresearch/application/League/Scorecard/hooks/useScorecardTable';
import { useScorecardPage } from '@/creditflowresearch/application/League/Scorecard/hooks/useScorecardPage';
import { IScorecardProps, IScorecardSetters, IScorecardDataExtracted } from '@/creditflowresearch/application/League/Scorecard/types';
import { IScorecardState } from '@/creditflowresearch/application/League/interfaces';
import { IProduct } from '@/creditflowresearch/domain/vo/Product';
import { IRegion } from '@/creditflowresearch/domain/vo/Region';
import { IStandingsPayload, IStandingsResponse } from '@/creditflowresearch/domain/vo/League/IStandings';

interface IUseScorecardOperationsProps {
  props: IScorecardProps,
  state: IScorecardState,
  stateActions: IScorecardSetters,
  periods: IScorecardDataExtracted['periods'],
  type: string,
  productsList: IProduct[],
  regionsList: IRegion[],
  getStandings: (payload: IStandingsPayload) => Promise<IStandingsResponse>,
}

interface IUseScorecardOperationsReturn {
  filterOperations: ReturnType<typeof useScorecardFilterOrchestrator>,
  tableOperations: ReturnType<typeof useScorecardTable>,
  pageOperations: ReturnType<typeof useScorecardPage>,
}

export const useScorecardOperations = ({
  props,
  state,
  stateActions,
  periods,
  type,
  productsList,
  regionsList,
  getStandings,
}: IUseScorecardOperationsProps): IUseScorecardOperationsReturn => {
  const filterOperations = useScorecardFilterOrchestrator(
    props,
    productsList,
    regionsList,
    getStandings,
    state.company.data,
  );

  const tableOperations = useScorecardTable({
    state: {
      columnState: state.columnState,
      selectedPeriod: state.selectedPeriod,
      expandedCategories: state.expandedCategories,
    },
    data: { periods, type },
    actions: { updateFilters: filterOperations.updateFilters },
    setters: {
      setColumnState: stateActions.setColumnState,
      setSelectedPeriod: stateActions.setSelectedPeriod,
      setExpandedCategories: stateActions.setExpandedCategories,
    },
  });

  const pageOperations = useScorecardPage({
    companyData: state.company.data,
    updateFilters: filterOperations.updateFilters,
    getStandings,
    stateActions,
  });

  return useMemo(() => ({
    filterOperations,
    tableOperations,
    pageOperations,
  }), [filterOperations, tableOperations, pageOperations]);
};
